import axios from "axios";
import { BACKEND, API } from "./backend";

export { API };

const client = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "X-User": localStorage.getItem("tq_user") || "operatore" },
});

export const setOperator = (name) => {
  localStorage.setItem("tq_user", name);
  client.defaults.headers["X-User"] = name;
};

export const api = {
  // ----- Public workbench -----
  analyze: async (emailText, files = []) => {
    const fd = new FormData();
    fd.append("email_text", emailText);
    files.forEach((f) => fd.append("attachments", f));
    const r = await client.post("/quote/analyze", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
  },
  getSession: async (id) => (await client.get(`/quote/${id}`)).data,
  patchSession: async (id, payload) =>
    (await client.patch(`/quote/${id}`, payload)).data,
  quickMessage: async (id) =>
    (await client.post(`/quote/${id}/quick-message`)).data,
  match: async (id) => (await client.post(`/quote/${id}/match`)).data,
  // R11/A — Bulk auto-match server-side (idempotent, skips incomplete silently)
  matchTutte: async (id) => (await client.post(`/quote/${id}/match-tutte`)).data,
  // R11/B — Auto-confirm 1-click ESATTI
  autoConfirmEsatti: async (id) =>
    (await client.post(`/quote/${id}/auto-confirm-esatti`)).data,
  matchVoce: async (id, voceIdx, voce) =>
    (
      await client.post(`/quote/${id}/match-voce`, {
        voce_idx: voceIdx,
        voce,
      })
    ).data,
  selectCandidates: async (id, selezioni) =>
    (await client.post(`/quote/${id}/select`, { selezioni })).data,
  csvUrl: (id) => `${API}/quote/${id}/csv`,
  // R10 v0.5.8 — totali offerta autocalcolati
  totals: async (id) => (await client.get(`/quote/${id}/totals`)).data,
  recent: async (limit = 5) =>
    (await client.get(`/quote/recent`, { params: { limit } })).data,
  byCustomer: async (name) =>
    (await client.get(`/quote/customer/${encodeURIComponent(name)}`)).data,

  // ----- Admin (cookie-protected) -----
  adminLogin: async (password) =>
    (await client.post(`/admin/auth`, { password })).data,
  adminLogout: async () => (await client.post(`/admin/logout`)).data,
  adminWhoami: async () => (await client.get(`/admin/whoami`)).data,
  listino: async ({ page = 1, page_size = 50, q = "", famiglia = "", missing = "" } = {}) => {
    const params = { page, page_size };
    if (q) params.q = q;
    if (famiglia) params.famiglia = famiglia;
    if (missing) params.missing = missing;
    return (await client.get("/admin/listino", { params })).data;
  },
  // R11/C4 — Backfill endpoints (super_admin only)
  listinoBackfillDnOut: async (dry_run = false) =>
    (await client.post(`/admin/listino/backfill-dn-out?dry_run=${dry_run}`)).data,
  listinoBackfillSpessore: async (dry_run = false) =>
    (await client.post(`/admin/listino/backfill-spessore?dry_run=${dry_run}`)).data,
  listinoReclassifyPollici: async (dry_run = false) =>
    (await client.post(`/admin/listino/reclassify-dn-pollici?dry_run=${dry_run}`)).data,
  listinoInlineEdit: async (codice, payload) =>
    (await client.patch(`/admin/listino/${codice}`, payload)).data,
  importListino: async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return (
      await client.post("/admin/listino/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  sessions: async ({ page = 1, page_size = 50, status = "", operator = "" } = {}) => {
    const params = { page, page_size };
    if (status) params.status = status;
    if (operator) params.operator = operator;
    return (await client.get("/admin/sessions", { params })).data;
  },
  matrice: async () => (await client.get("/admin/matrice")).data,
  health: async () => (await client.get("/admin/health")).data,

  // ----- R5 —————————————————————————————————————————————
  confirmInference: async (offerId, voceIdx, field, choice) =>
    (
      await client.post(
        `/offers/${offerId}/voci/${voceIdx}/confirm-inference`,
        { field, choice }
      )
    ).data,
  listinoAlternatives: async (famiglia, targetParams, limit = 6) => {
    const params = { famiglia, limit };
    Object.entries(targetParams || {}).forEach(([k, v]) => {
      if (v) params[k] = v;
    });
    return (await client.get("/listino/alternatives", { params })).data;
  },
  draftEmailSave: async (offerId, payload) =>
    (await client.post(`/offers/${offerId}/draft-email`, payload)).data,
  draftEmailGet: async (offerId) =>
    (await client.get(`/offers/${offerId}/draft-email`)).data,
  preferencesApply: async (emailDomain) =>
    (
      await client.get(
        `/customers/${encodeURIComponent(emailDomain)}/preferences-apply`
      )
    ).data,
  learningRulesList: async () => (await client.get("/learning-rules")).data,
  learningRuleCreate: async (rule) =>
    (await client.post("/learning-rules", rule)).data,
  learningRuleDelete: async (ruleId) =>
    (await client.delete(`/learning-rules/${ruleId}`)).data,
  learningRuleParse: async (text) =>
    (await client.post("/learning-rules/parse", { text })).data,

  // ----- R6 —————————————————————————————————————————————
  autoProfile: async (emailDomain, force = false) =>
    (
      await client.get(
        `/customers/${encodeURIComponent(emailDomain)}/auto-profile`,
        { params: { force } }
      )
    ).data,
  conversationReply: async (offerId, replyText) =>
    (
      await client.post(
        `/offers/${offerId}/conversation/customer-reply`,
        { reply_text: replyText }
      )
    ).data,
  conversationThread: async (offerId) =>
    (await client.get(`/offers/${offerId}/conversation/thread`)).data,
  visionOcr: async (offerId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return (
      await client.post(
        `/offers/${offerId}/attachments/ocr-vision`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
    ).data;
  },
  pdfTables: async (offerId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return (
      await client.post(
        `/offers/${offerId}/attachments/pdf-tables`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
    ).data;
  },
  pdfTablesImport: async (offerId, rows, defaultFamiglia = "F05") =>
    (
      await client.post(
        `/offers/${offerId}/attachments/pdf-tables/import`,
        { rows, default_famiglia: defaultFamiglia }
      )
    ).data,
  companyTemplatesList: async () =>
    (await client.get("/admin/company-templates")).data,
  companyTemplateUpload: async (formData) =>
    (
      await client.post("/admin/company-templates", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data,
  companyTemplateDelete: async (tplId) =>
    (await client.delete(`/admin/company-templates/${tplId}`)).data,

  // ----- R11.b — Generic REST helpers used by AdminLearningRules / LearningRuleModal -----
  get: async (path) => (await client.get(path)).data,
  post: async (path, payload) => (await client.post(path, payload)).data,
  patch: async (path, payload) => (await client.patch(path, payload)).data,
  del: async (path) => (await client.delete(path)).data,
};

export const formatErrorDetail = (error) => {
  const detail = error?.response?.data?.detail;
  if (detail == null) return "Servizio temporaneamente non disponibile, riprova tra qualche secondo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};
